import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';

// Enhanced caching service for Phase 3 optimizations
class CacheService {
  private static instance: CacheService;
  private queryClient: QueryClient | null = null;
  private cachePersistence: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();

  private constructor() {}

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  initialize(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.loadPersistedCache();
  }

  // Persistent cache management
  private loadPersistedCache() {
    try {
      const cached = localStorage.getItem('ameup_cache');
      if (cached) {
        const data = JSON.parse(cached);
        this.cachePersistence = new Map(Object.entries(data));
        this.cleanExpiredEntries();
      }
    } catch (error) {
      console.warn('Error loading persisted cache:', error);
    }
  }

  private persistCache() {
    try {
      const cacheObject = Object.fromEntries(this.cachePersistence);
      localStorage.setItem('ameup_cache', JSON.stringify(cacheObject));
    } catch (error) {
      console.warn('Error persisting cache:', error);
    }
  }

  private cleanExpiredEntries() {
    const now = Date.now();
    for (const [key, entry] of this.cachePersistence.entries()) {
      if (now > entry.timestamp + entry.ttl) {
        this.cachePersistence.delete(key);
      }
    }
    this.persistCache();
  }

  // Set data in persistent cache
  setCache(key: string, data: any, ttl: number = 5 * 60 * 1000) {
    this.cachePersistence.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    this.persistCache();
  }

  // Get data from persistent cache
  getCache(key: string): any | null {
    const entry = this.cachePersistence.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      this.cachePersistence.delete(key);
      this.persistCache();
      return null;
    }

    return entry.data;
  }

  // Clear specific cache key
  clearCache(key: string) {
    this.cachePersistence.delete(key);
    this.persistCache();
  }

  // Clear all persistent cache
  clearAllCache() {
    this.cachePersistence.clear();
    localStorage.removeItem('ameup_cache');
  }

  // Preload frequently accessed data
  async preloadEssentialData() {
    if (!this.queryClient) return;

    const preloadPromises = [
      // Preload featured suppliers
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.suppliers.featured,
        staleTime: 10 * 60 * 1000,
      }),
      // Preload tax data
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.tax.checkout,
        staleTime: 30 * 60 * 1000,
      }),
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.tax.platform,
        staleTime: 30 * 60 * 1000,
      }),
    ];

    try {
      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.warn('Error preloading essential data:', error);
    }
  }

  // Optimistic updates for better UX
  optimisticUpdate<T>(queryKey: any[], updater: (old: T | undefined) => T) {
    if (!this.queryClient) return;

    this.queryClient.setQueryData(queryKey, updater);
  }

  // Batch invalidations for efficiency
  batchInvalidate(queryKeys: any[][]) {
    if (!this.queryClient) return;

    queryKeys.forEach(key => {
      this.queryClient!.invalidateQueries({ queryKey: key });
    });
  }

  // Smart prefetching based on user behavior
  smartPrefetch(userContext: {
    currentRoute?: string;
    userRole?: string;
    recentActions?: string[];
  }) {
    if (!this.queryClient) return;

    const { currentRoute, userRole, recentActions } = userContext;

    // Route-based prefetching
    if (currentRoute === '/fornecedores') {
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.suppliers.all,
        staleTime: 5 * 60 * 1000,
      });
    }

    if (currentRoute?.includes('/infos-festa')) {
      // Prefetch conversation data if viewing party info
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.conversations.all,
        staleTime: 2 * 60 * 1000,
      });
    }

    // Role-based prefetching
    if (userRole === 'admin') {
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.admin.metrics,
        staleTime: 5 * 60 * 1000,
      });
      this.queryClient.prefetchQuery({
        queryKey: queryKeys.admin.security,
        staleTime: 2 * 60 * 1000,
      });
    }

    if (userRole === 'supplier') {
      // Prefetch supplier-specific data
      recentActions?.forEach(action => {
        if (action.includes('view_products')) {
          this.queryClient!.prefetchQuery({
            queryKey: queryKeys.products.all,
            staleTime: 5 * 60 * 1000,
          });
        }
      });
    }
  }

  // Cache warming strategies
  warmCache(strategy: 'essential' | 'user-specific' | 'full' = 'essential') {
    switch (strategy) {
      case 'essential':
        this.preloadEssentialData();
        break;
      case 'user-specific':
        // Implement user-specific warming based on history
        break;
      case 'full':
        // Implement full cache warming (use sparingly)
        break;
    }
  }

  // Cache statistics
  getCacheStats() {
    const stats = {
      persistentCacheSize: this.cachePersistence.size,
      queryClientCacheSize: this.queryClient?.getQueryCache().getAll().length || 0,
      persistentCacheKeys: Array.from(this.cachePersistence.keys()),
    };

    return stats;
  }

  // Health check for cache performance
  healthCheck() {
    const stats = this.getCacheStats();
    const health = {
      status: 'healthy' as 'healthy' | 'warning' | 'critical',
      issues: [] as string[],
      recommendations: [] as string[],
    };

    if (stats.persistentCacheSize > 100) {
      health.status = 'warning';
      health.issues.push('Large persistent cache size');
      health.recommendations.push('Consider clearing old cache entries');
    }

    if (stats.queryClientCacheSize > 200) {
      health.status = 'warning';
      health.issues.push('Large query client cache');
      health.recommendations.push('Review query stale times');
    }

    return health;
  }
}

export const cacheService = CacheService.getInstance();